import React, { useState, useCallback, useMemo } from 'react';
import { Card, Typography, Button, showToast, Input } from '@attentive-platform/stem-ui';
import { isValidEmail, transformTradeDataToUpdatePayload } from '../../Utils/HelperFunctions';
import { TradeTable } from '../TradeTable';
import { useUpdateTrades } from '../../Utils/services';
import { useGetCompanyTradeData } from '../../Hooks/useGetCompanyTradeData';

const UpdateTrade = () => {
  const [data, setData] = useState([]);
  const [apiData, setApiData] = useState([]);
  const [email, setEmail] = useState('');
  const [tradesFetched, setTradesFetched] = useState(false);

  const { loading: companyTradeLoading, fetchCompanyTrades: originalFetchCompanyTrades } = useGetCompanyTradeData({
    onTradeDataChange: setData,
    onFetchApiData: setApiData
  });

  const fetchCompanyTrades = useCallback(
    async email => {
      await originalFetchCompanyTrades(email);
      setTradesFetched(true);
    },
    [originalFetchCompanyTrades]
  );

  const handleEmailChange = useCallback(
    e => {
      setEmail(e.target.value);
      setTradesFetched(false);
    },
    []
  );

  const { mutate, loading: updateTradesLoading } = useUpdateTrades();

  const handleUpdateTrades = useCallback(() => {
    if (!isValidEmail(email)) {
      showToast('Please enter a valid email', 'error');
      return;
    }

    if (data.some(item => item.trade === '' || item.workflow.length === 0)) {
      showToast('Please select at least one trade and takeoff type', 'error');
      return;
    }

    const payload = transformTradeDataToUpdatePayload(data, apiData);

    mutate(
      { data: { admin_email: email, trade_configs: payload } },
      {
        onSuccess: data => {
          showToast('Trades updated successfully');
          if (data?.company_trade_configs) setApiData(data?.company_trade_configs || []);
        },
        onError: error => {
          showToast(error, 'error');
        }
      }
    );
  }, [data, email, mutate, apiData]);

  const disableUpdateButton = useMemo(() => {
    return updateTradesLoading || !email || transformTradeDataToUpdatePayload(data, apiData).length === 0;
  }, [updateTradesLoading, email, data, apiData]);

  return (
    <Card className='p-4 w-[480px] '>
      <Typography variant='h6' className='mb-2'>
        Update Trade
      </Typography>
      <div className='mb-3 text-left'>
        <Typography variant='body3'>Admin Email</Typography>
        <div className='flex gap-2'>
          <Input
            className='flex-1'
            value={email}
            onChange={handleEmailChange}
            placeholder='Admin Email'
            inputProps={{
              type: 'email',
              placeholder: 'Admin Email',
              'aria-label': 'Email',
              required: true
            }}
          />
          <Button
            variant='contained'
            color='primary'
            onClick={() => fetchCompanyTrades(email)}
            loading={companyTradeLoading}
            disabled={!email || !isValidEmail(email)}>
            Fetch
          </Button>
        </div>
      </div>
      <div className='mb-3 text-left'>
        <Typography variant='body3'>Select Trades and takeoff types</Typography>
        <TradeTable
          data={data}
          onDataChange={setData}
          loading={companyTradeLoading}
          disableAdd={!email || !isValidEmail(email) || companyTradeLoading || !tradesFetched}
        />
      </div>
      <div className='flex justify-end'>
        <Button
          disabled={disableUpdateButton}
          color='primary'
          variant='contained'
          onClick={handleUpdateTrades}
          loading={updateTradesLoading}>
          Update trades
        </Button>
      </div>
    </Card>
  );
};

export default UpdateTrade;
